import { ReactElement, useState, useCallback } from 'react';
import styled from '../../Styles/themed-styled-components';

import { Announcement, AnnouncementType, Language } from '@breathelife/types';

import { ChevronDownIcon } from '../../Assets/Icons/ChevronDownIcon';
import { getLanguage } from '../../Localization/utils';

type AnnouncementBannerProps = {
  announcements: Announcement[];
};

type BannerStyleProps = {
  $type: AnnouncementType;
};

const getBannerColors = (
  type: AnnouncementType,
): { background: string; text: string; border: string; iconColor: string } => {
  switch (type) {
    case AnnouncementType.warning:
      return {
        background: '#FDEAD1',
        text: '#333333',
        border: '#F59E0B',
        iconColor: '#D97706',
      };
    case AnnouncementType.success:
      return {
        background: '#D1FAE5',
        text: '#333333',
        border: '#10B981',
        iconColor: '#059669',
      };
    case AnnouncementType.error:
      return {
        background: '#F6B1B1',
        text: '#333333',
        border: '#861010',
        iconColor: '#861010',
      };
    case AnnouncementType.info:
    default:
      return {
        background: '#DBEAFE',
        text: '#333333',
        border: '#3B82F6',
        iconColor: '#2563EB',
      };
  }
};

const BannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
`;

const BannerItem = styled.div<BannerStyleProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 24px;
  background-color: ${(props) => getBannerColors(props.$type).background};
  color: ${(props) => getBannerColors(props.$type).text};
  border: 1px solid ${(props) => getBannerColors(props.$type).border};
  border-radius: 4px;
  margin: 0 16px;
`;

const BannerContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const BannerText = styled.div<{ $isExpanded: boolean }>`
  display: ${(props) => (props.$isExpanded ? 'block' : 'none')};
  font-size: 13px;
  line-height: 1.5;

  p {
    margin: 0;
  }
`;

const IconButton = styled.button<BannerStyleProps>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    stroke: ${(props) => getBannerColors(props.$type).iconColor};
  }
`;

const RotatableIcon = styled.span<{ $rotated: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.$rotated ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
`;

type SingleAnnouncementProps = {
  announcement: Announcement;
  language: Language;
};

function SingleAnnouncement({ announcement, language }: SingleAnnouncementProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev: boolean) => !prev);
  }, []);

  const title = announcement.title[language] || announcement.title.en || '';
  const text = announcement.text[language] || announcement.text.en || '';
  const hasText = text.trim().length > 0;

  return (
    <BannerItem $type={announcement.type}>
      <BannerContent>
        <BannerTitle dangerouslySetInnerHTML={{ __html: title }} />
        {hasText && <BannerText $isExpanded={isExpanded} dangerouslySetInnerHTML={{ __html: text }} />}
      </BannerContent>
      {hasText && (
        <IconButton $type={announcement.type} onClick={handleToggle} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <RotatableIcon $rotated={!isExpanded}>
            <ChevronDownIcon svgProps={{ width: 16, height: 16 }} />
          </RotatableIcon>
        </IconButton>
      )}
    </BannerItem>
  );
}

export function AnnouncementBanner({ announcements }: AnnouncementBannerProps): ReactElement | null {
  const language = getLanguage();

  if (announcements.length === 0) {
    return null;
  }

  return (
    <BannerContainer>
      {announcements.map((announcement) => (
        <SingleAnnouncement key={announcement.id} announcement={announcement} language={language} />
      ))}
    </BannerContainer>
  );
}
